import { atom, atomFamily, selector, selectorFamily } from "recoil"
import { localUserIdAtom } from "../users"
import {
  MessageModel,
  ActionChat,
  ReplyModel,
  ReactionsStatusAction,
  ChatModel,
  LastReactionProps
} from "./types"
import { notificationNewMessagesAtom } from "../notification"
import { overlaySelector } from "../overlay"
import { addOrUpdateList, getUniqueList } from "../../../util"
import { v4 as uuidv4 } from "uuid"

interface ChatCallbackSelectorProps {
  handleReturnData?: () => Promise<string[]>
}

interface ChatActionsSelectorProps {
  getChatDataById?: (id: string | number) => Promise<ChatModel>
}

interface isFirstTimeChatResponseCallbackSelectorProps {
  handleReturnData?: () => Promise<boolean>
}

interface ReplyIdsCallbackSelectorProps {
  handleReturnData?: () => Promise<{ id: string; parentId: string }[]>
}

export const chatsAtom = atomFamily({
  key: "chats",
  default: null
})

const chatIdsAtom = atom<string[]>({
  key: "chatIds",
  default: []
})

export const allReplysIdsAtom = atom<{ id: string; parentId: string }[]>({
  key: "allReplysIdsAtom",
  default: []
})

export const chatIsReadAtom = atom<boolean>({
  key: "chatIsReadAtom",
  default: false
})

export const chatCountAtom = atom<number>({
  key: "chatCountAtom",
  default: 0
})

export const prevChatCountAtom = atom<number>({
  key: "prevChatCountAtom",
  default: 0
})

export const selectedReplyAtom = atom<{ id: string; replyId?: string }>({
  key: "selectedReplyAtom",
  default: null
})

export const newReplyAtom = atom<{ id: string; parentChatId: string }>({
  key: "newReplyAtom",
  default: null
})

export const newRepliesAtom = atom<{ id: string; parentId: string }[]>({
  key: "newRepliesAtom",
  default: null
})

export const prevNewReplyAtom = atom<{ id: string; parentId: string }>({
  key: "prevNewReplyAtom",
  default: { id: "0", parentId: "0" }
})

export const newMessagesAtom = atom<{ id: string; parentId: string }[]>({
  key: "newMessagesAtom",
  default: null
})

export const newMessageId = atom<string>({
  key: "newMessageId",
  default: null
})

export const isClearChatAtom = atom<boolean>({
  key: "isClearChatAtom",
  default: false
})

export const isFirstTimeChatResponseAtom = atom<boolean>({
  key: "isFirstTimeChatResponseAtom",
  default: true
})

export const chatScrollAtom = atom({
  key: "chatScrollAtom",
  default: null
})

export const topMostUnreadAtom = atom<{
  type: "Message" | "Reply"
  id: number
  parentId: number
}>({
  key: "topMostUnreadAtom",
  default: null
})

export const bottomMostUnreadAtom = atom<{
  type: "Message" | "Reply"
  id: number
  parentId: number
}>({
  key: "bottomMostUnreadAtom",
  default: null
})

export const chatIsReadSelector = selector<boolean>({
  key: "chatIsReadSelector",
  get: ({ get }) => {
    return get(chatIsReadAtom)
  },
  set({ set, get }, value) {
    if (value) {
      const isBottomMostUnread = get(bottomMostUnreadAtom)
      const isTopMostUnread = get(topMostUnreadAtom)
      if (!isBottomMostUnread && !isTopMostUnread) {
        set(chatIsReadAtom, true)
      }
    } else {
      set(chatIsReadAtom, false)
    }
  }
})

export const chatScrollStateAtom = atom<"UP" | "DOWN" | "DONE">({
  key: "chatScrollStateAtom",
  default: "DONE"
})

export const lastReactionAtom = atom<LastReactionProps>({
  key: "lastReactionsAtom",
  default: {
    type: "message",
    id: "",
    parentChatId: ""
  }
})

// abstraction
export const chatsSelector = selectorFamily<Partial<MessageModel>, string>({
  key: "chats-access-by-id",
  get:
    id =>
    ({ get }) => {
      return get(chatsAtom(id))
    },
  set:
    id =>
    ({ set }, attributes /* : Partial<MessageModel> | DefaultValue */) => {
      return set(chatsAtom(id), chat => ({ ...chat, ...attributes }))
    }
})

export const updateChatSelector = selector<Partial<MessageModel>>({
  key: "update-chat-by-id",
  get: () => null,
  set: ({ set }, attributes: Partial<MessageModel>) => {
    console.log(attributes)
    if (attributes?.id) {
      const toUpdate = chatsAtom(attributes?.id)
      set(toUpdate, chat => ({ ...chat, ...attributes }))
    }
  }
})

const sortReplyChat = (messages: ChatModel["replyChats"]) => {
  if (!messages || messages.length === 0) return []
  const sortedMessages = [...messages]
  sortedMessages.sort((x, y) => {
    const date1 = new Date(x?.timestamp)
    const date2 = new Date(y?.timestamp)
    return +date1 - +date2
  })
  return sortedMessages
}

/** Add and Update if message is already present */
const addChatData = ({ set, get, reset }: any, payloads: any[]) => {
  const localUserId = get(localUserIdAtom)
  const isFirstTimeChatResponse = get(isFirstTimeChatResponseAtom)
  const overlay = get(overlaySelector)
  const newChatListIds = []
  const newReplyChatListIds = []
  let chatRepliesId = []
  let parentReplyIds = {}

  if (isFirstTimeChatResponse) {
    // set to false has we have received first message
    set(isFirstTimeChatResponseAtom, false)
  }

  payloads.forEach(payload => {
    // Check if message is a replied message
    if (payload.parentChatId !== undefined) {
      // Add the current reply message in the array of replies for that parent id
      // key will be parent id and value will be arrray of replies
      parentReplyIds = {
        ...parentReplyIds,
        [payload.parentChatId]: [
          ...(parentReplyIds[payload.parentChatId] ?? []),
          payload
        ]
      }

      newReplyChatListIds.push({
        id: payload.id,
        parentId: payload.parentChatId
      })

      // If the reply is new message, then add it to newReplayAtom and play notification sound
      if (payload.author?.id !== localUserId && !isFirstTimeChatResponse) {
        set(newReplyAtom, {
          id: payload.id,
          parentChatId: payload.parentChatId
        })

        set(notificationNewMessagesAtom, prev => {
          return {
            ...prev,
            isSoundNewMessage: true
          }
        })
      }
    }

    // Check if current message is parent message
    if (payload.parentChatId === undefined) {
      let message = {
        ...payload,
        // if replied is present in payload, apply sort
        replyChats: payload?.replyChats
          ? sortReplyChat(payload.replyChats)
          : [],
        participants: [payload?.author?.id]
      }

      // If current message is published as a overlay, then mark published as true
      if (overlay && overlay?.type === "chat" && overlay?.id === payload?.id) {
        message.published = true
      }

      // Add the current message into a recoil state
      set(chatsAtom(payload.id), chat => ({ ...chat, ...message }))
      newChatListIds.push(payload.id)

      // Save the replies id into chatRepliesId
      const replyChatIds = payload?.replyChats?.map(chat => ({
        id: chat.id,
        parentId: payload.id
      }))
      if (replyChatIds) {
        chatRepliesId = chatRepliesId.concat(replyChatIds)
      }

      // Set has new message id and notify user about it
      if (payload.author?.id !== localUserId && !isFirstTimeChatResponse) {
        set(newMessageId, payload.id)
        set(notificationNewMessagesAtom, prev => {
          return {
            ...prev,
            isSoundNewMessage: true
          }
        })
      }
    }
  })

  if (newReplyChatListIds.length !== 0) {
    for (let parentChatId in parentReplyIds) {
      const payloads = parentReplyIds[parentChatId]
      set(chatsAtom(parentChatId), chat => {
        if (!chat) return {}
        const replysArray: ReplyModel[] = chat.replyChats ?? []
        const replys = addOrUpdateList(
          replysArray,
          payloads,
          (reply1, reply2) => reply1.id === reply2.id
        )

        const participants: string[] = [...chat.participants].concat(
          payloads.map(payload => payload.author.id)
        )

        return { ...chat, replyChats: replys, participants: participants }
      })
    }

    set(newRepliesAtom, newReplyChatListIds)
  }

  if (newReplyChatListIds.length !== 0 || chatRepliesId.length !== 0) {
    set(allReplysIdsAtom, replyIds => {
      return addOrUpdateList(
        replyIds,
        [...newReplyChatListIds, ...chatRepliesId],
        (reply1, reply2) => reply1.id === reply2.id
      )
    })
  }

  if (newChatListIds.length !== 0) {
    set(newMessagesAtom, newChatListIds)
  }

  if (isFirstTimeChatResponse) {
    set(chatIdsAtom, chatIds => {
      // remove the chats which is not present in newChatListIds
      chatIds.forEach(chatId => {
        if (!newChatListIds.includes(chatId)) {
          reset(chatsAtom(chatId))
        }
      })
      return newChatListIds
    })
  } else {
    if (newChatListIds.length !== 0)
      set(chatIdsAtom, chatIds =>
        getUniqueList([...chatIds, ...newChatListIds])
      )
  }
}

export const localUserMessageResponseToken = atom({
  key: "localUserMessageResponseToken",
  default: null
})

export const chatIdsSelector = selector({
  key: "chatIdsSelector",
  get: ({ get }) => {
    return get(chatIdsAtom)
  },
  set: ({ set, reset, get }, action) => {
    const { type, payload } = action as ActionChat

    if (
      payload?.data?.length &&
      payload.data[0] &&
      payload.data[0].attachmentURL
    ) {
      if (payload.data[0].parentChatId) {
        set(chatAttachmentReplyDataAtom, { data: "", mimeType: "" })
        set(isSavingChatAttachmentReply, false)
      } else {
        set(chatAttachmentDataAtom, { data: "", mimeType: "" })
        set(isSavingChatAttachment, false)
      }

      set(recentUploadAttachmentUrlAtom, payload.data[0].attachmentURL)
    }

    if (type === "ADD_ALL") {
      if (payload && Array.isArray(payload.data) && payload.data.length > 0) {
        addChatData(
          { set, reset, get },
          payload.data as ActionChat["payload"][]
        )

        const localUserId = get(localUserIdAtom)
        const res = payload.data[0]
        const author = (res as unknown as { author: { id: string } }).author
        if (author.id === localUserId) {
          set(localUserMessageResponseToken, uuidv4())
        }
        return
      }
    }

    // TODO: check if we use ADD action
    if (type === "ADD") {
      const localUserId = get(localUserIdAtom)
      const isFirstTimeChatResponse = get(isFirstTimeChatResponseAtom)
      if (payload.message.parentChatId !== undefined) {
        set(chatsAtom(payload.message.parentChatId), chat => {
          const replysArray = chat.replyChats ?? []
          // approve message need sorting
          const replys: ReplyModel[] = sortReplyChat(
            [...replysArray].concat(payload.message)
          )
          const participants: string[] = [...chat.participants].concat(
            payload.message.author.id
          )

          return { ...chat, replyChats: replys, participants: participants }
        })

        set(allReplysIdsAtom, replyIds => [
          ...replyIds,
          { id: payload.message.id, parentId: payload.message.parentChatId }
        ])

        if (
          payload.message.author?.id !== localUserId &&
          !isFirstTimeChatResponse
        ) {
          set(newRepliesAtom, [
            {
              id: payload.message.id,
              parentId: payload.message.parentChatId
            }
          ])

          set(notificationNewMessagesAtom, prev => {
            return {
              ...prev,
              isSoundNewMessage: true
            }
          })
        }
      }

      if (payload.message.parentChatId === undefined) {
        const message = {
          ...payload.message,
          replyChats: sortReplyChat(payload.message?.replyChats),
          participants: [payload.message?.author?.id]
        }

        set(chatsAtom(payload.message.id), message)
        set(chatIdsAtom, chatIds => [...chatIds].concat(payload.message.id))
        const replyChatIds = payload.message?.replyChats?.map(chat => ({
          id: chat.id,
          parentId: payload.message.id
        }))
        if (replyChatIds) {
          set(allReplysIdsAtom, replyIds => [...replyIds, ...replyChatIds])
        }
        //Scroll chat bottom
        if (payload.message.author?.id === localUserId) {
          set(chatScrollAtom, true)
        }
        //
        if (
          payload.message.author?.id !== localUserId &&
          !isFirstTimeChatResponse
        )
          set(newMessageId, payload.message.id)
        set(notificationNewMessagesAtom, prev => {
          return {
            ...prev,
            isSoundNewMessage: true
          }
        })
      }
    }

    if (type === "UPDATE") {
      const messagesIds = get(chatIdsAtom)
      const isMessage = messagesIds.includes(payload.id)

      if (isMessage) {
        set(chatsAtom(payload.id), chat => ({ ...chat, ...payload.attributes }))
      } else {
        // update reply chat
        const allReplysId = get(allReplysIdsAtom)
        const target = allReplysId.find(item => item.id === payload.id)
        if (!target) return

        set(chatsAtom(target.parentId), chat => {
          const replys = chat.replyChats?.map(reply => {
            if (reply.id === payload.id) {
              return { ...reply, ...payload.attributes }
            }
            return reply
          })
          return { ...chat, replyChats: replys }
        })
      }
    }

    if (type === "UPDATE_REPLY") {
      // Check if reply id is present then update
      const allReplysId = get(allReplysIdsAtom)
      const target = allReplysId.find(item => item.id === payload.id)
      if (!target) return

      set(chatsAtom(payload.parentChatId), chat => {
        const replys = chat.replyChats?.map(reply => {
          if (reply.id === payload.id) {
            return { ...reply, ...payload.attributes }
          }
          return reply
        })
        return { ...chat, replyChats: replys }
      })
    }

    if (type === "REMOVE") {
      const messagesIds = get(chatIdsAtom)
      const isMessage = messagesIds.includes(payload.id)

      if (isMessage) {
        reset(chatsAtom(payload.id))
        set(chatIdsAtom, prev => prev.filter(chatId => chatId !== payload.id))
        // Removing all reply id
        set(allReplysIdsAtom, item =>
          item.filter(item => item.parentId !== payload.id)
        )
      } else {
        // delete reply ids
        const allReplysId = get(allReplysIdsAtom)
        const target = allReplysId.find(item => item.id === payload.id)
        if (!target) return

        set(chatsAtom(target.parentId), message => {
          const authorReplyId = message.replyChats.filter(
            item => item.id === payload.id
          )[0].author.id

          const filterReplys = message.replyChats.filter(
            reply => reply.id !== payload.id
          )
          const filterParticipants = message.participants.filter(
            item => item !== authorReplyId
          )
          return {
            ...message,
            replyChats: filterReplys,
            participants: filterParticipants
          }
        })
        // Removing id from allReplyIdsAtom
        set(allReplysIdsAtom, item =>
          item.filter(item => item.id !== payload.id)
        )
      }
    }

    if (type === "ADD_REACTION_REPLY") {
      set(chatsAtom(payload.attributes.parentChatId), chat => {
        const replys = chat.replyChats

        const newReplys = replys.map(reply => {
          if (reply.id === payload.id) {
            return { ...reply, reactions: payload.attributes.reactions }
          }
          return reply
        })
        return { ...chat, ...{ replyChats: newReplys } }
      })
    }

    if (type === "ADD_BAD_MESSAGE") {
      set(chatsAtom(payload.message.id), payload.message)
      set(chatIdsAtom, chatIds => [...chatIds].concat(payload.message.id))
    }

    if (type === "ADD_BAD_REPLY") {
      if (payload.message.parentChatId !== undefined) {
        set(chatsAtom(payload.message.parentChatId), chat => {
          const replys: ReplyModel[] = [...chat.replyChats].concat(
            payload.message
          )

          return { ...chat, replyChats: replys }
        })
        set(allReplysIdsAtom, replyIds => [
          ...replyIds,
          { id: payload.message.id, parentId: payload.message.parentChatId }
        ])
      }
    }

    if (type === "REMOVE_REPLYS_IDS") {
      set(allReplysIdsAtom, replyIds => {
        const result = replyIds.filter(
          item => !payload.replysIds.includes(item.id)
        )
        return result
      })
    }

    if (type === "REMOVE_ALL") {
      set(chatIdsAtom, [])
      set(allReplysIdsAtom, [])
    }

    if (type === "RESET") {
      let chatArr = get(chatIdsAtom)
      for (let chat of chatArr) {
        reset(chatsAtom(chat))
      }
      reset(chatIdsAtom)
      reset(allReplysIdsAtom)
    }
  }
})

export const currentChatGenerationAtom = atom<number>({
  key: "currentChatGenerationAtom",
  default: 0
})

export const filterAvatarChatsByIdAtom = atom<string | null>({
  key: "filterAvatarChatsByIdAtom",
  default: null
})

//This is User Object List used in the Participants List
export const chatListSelector = selector({
  key: "chatListSelector",
  get: ({ get }) => {
    const chatIdsList = get(chatIdsAtom)
    const filterAvatarChatsById = get(filterAvatarChatsByIdAtom)
    const generation = get(currentChatGenerationAtom)
    let chats = chatIdsList
      .map(id => get(chatsAtom(id)))
      .sort((a, b) => (a?.timestamp < b?.timestamp ? -1 : 1))
    if (filterAvatarChatsById != null) {
      chats = chats.filter(
        chat =>
          filterAvatarChatsById == chat.toAvatarId &&
          chat.generation === generation
      )
    }
    return chats
  }
})

export const reactionsStatusAtom = atom(
  /* <[string, string[]][]> */ {
    key: "reactionsStatusReacoilAtom",
    default: []
  }
)

export const reactionsStatusSelector = selector({
  key: "reactionsStatussReacoilSelector",
  get: ({ get }) => {
    return get(reactionsStatusAtom)
  },
  set: ({ set, get }, action) => {
    const { payload, type } = action as ReactionsStatusAction

    if (type === "DOWNLOAD") {
      set(reactionsStatusAtom, [...payload.reactionsList])
    }

    if (type === "UPDATE") {
      set(reactionsStatusAtom, prev => {
        const result = prev.map(item => {
          if (item[0] === payload.id) {
            return [payload.id, payload.reactions]
          }
          return item
        })
        return result
      })
    }

    if (type === "ADD") {
      set(reactionsStatusAtom, [...get(reactionsStatusAtom), payload])
    }

    if (type === "DELETE") {
      set(reactionsStatusAtom, prev => {
        const array = prev.map(item => {
          if (item[0] === payload.id) {
            const reactions = item[1].filter(item => item !== payload.reaction)
            return [item[0], reactions]
          }
          return item
        })

        const result = array.filter(item => item[1].length !== 0)

        return result
      })
    }
  }
})

export const chatCallbackSelector = selector<ChatCallbackSelectorProps>({
  key: "ChatCallbackSelectorProps",
  get: ({ getCallback }) => {
    const handleReturnData = getCallback(({ snapshot }) => async () => {
      const data = await snapshot.getPromise(chatIdsAtom)

      return data
    })
    return { handleReturnData }
  }
})

export const isFirstTimeChatResponseCallbackSelector =
  selector<isFirstTimeChatResponseCallbackSelectorProps>({
    key: "isFirstTimeChatResponseCallbackSelector",
    get: ({ getCallback }) => {
      const handleReturnData = getCallback(({ snapshot }) => async () => {
        const data = await snapshot.getPromise(isFirstTimeChatResponseAtom)

        return data
      })
      return { handleReturnData }
    }
  })

export const replyIdsCallbackSelector = selector<ReplyIdsCallbackSelectorProps>(
  {
    key: "replyIdsCallbackSelector",
    get: ({ getCallback }) => {
      const handleReturnData = getCallback(({ snapshot }) => async () => {
        const data = await snapshot.getPromise(allReplysIdsAtom)

        return data
      })
      return { handleReturnData }
    }
  }
)

export const chatActionsSelector = selector<ChatActionsSelectorProps>({
  key: "chatActionsSelector",
  get: ({ getCallback }) => {
    const getChatDataById = getCallback(
      ({ snapshot }) =>
        async (id: string | number) => {
          const data = await snapshot.getPromise(chatsAtom(id))

          return data as ChatModel
        }
    )
    return { getChatDataById }
  }
})

export type Attachment = { data: string; mimeType: string }

export const chatAttachmentDataAtom = atom<Attachment>({
  key: "chatAttachmentDataAtom",
  default: {
    data: "",
    mimeType: ""
  }
})

const chatAttachmentHeightAtom = atom<number>({
  key: "chatAttachmentHeightAtom",
  default: 0
})

export const chatAttachmentHeightSelector = selector<number>({
  key: "chatAttachmentHeightSelector",
  get: ({ get }) => {
    const attachment = get(chatAttachmentDataAtom)
    const hasAttachment = !!attachment.data
    const chatAttachmentHeight = get(chatAttachmentHeightAtom)

    // Return chatAttachmentHeight if attach is present
    return hasAttachment ? chatAttachmentHeight : 0
  },
  set: ({ set }, newValue) => {
    set(chatAttachmentHeightAtom, newValue)
  }
})

export const recentUploadAttachmentUrlAtom = atom<string | null>({
  key: "recentUploadAttachmentUrlAtom",
  default: null
})

export const chatAttachmentReplyDataAtom = atom<Attachment>({
  key: "chatAttachmentReplyDataAtom",
  default: {
    data: "",
    mimeType: ""
  }
})

export const isSavingChatAttachment = atom<boolean>({
  key: "isSavingChatAttachment",
  default: false
})

export const isSavingChatAttachmentReply = atom<boolean>({
  key: "isSavingChatAttachmentReply",
  default: false
})

export const previewAttachmentId = atom<string>({
  key: "previewAttachmentId",
  default: ""
})

export const previouslyPublishedChat = atom<Partial<MessageModel>>({
  key: "previouslyPublishedChat",
  default: null
})
